import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  CircularProgress,
  MenuItem,
  SxProps,
  TextField,
  Theme,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { PositionGeocode } from '@/domain/position-geocode/position-geocode.entity';
import { debounce } from '@/ui/utils/debounce';

import { fetchOptionsFromApiPhoton } from './FSPositionGeocodeField.utils/fetch-options-from-api-photon';

export type Option = {
  key: string;
  position: PositionGeocode;
};

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

export function FSPositionGeocodeField({
  label,
  placeholder,
  defaultValue,
  value,
  sx,
  required,
  onChange,
  error,
  helperText,
  size = 'medium',
}: {
  label?: string;
  placeholder?: string;
  defaultValue?: PositionGeocode;
  value?: PositionGeocode;
  sx?: SxProps<Theme>;
  required?: boolean;
  onChange?: (value: PositionGeocode | null) => void;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}) {
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(
    value?.properties.name ?? defaultValue?.properties.name ?? '',
  );

  useEffect(() => {
    // this is usefull for RHF reset
    if (!value) {
      setInputValue('');
      setOptions([]);
    }
  }, [value]);

  const onInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      debouncedUpdateOptions(newInputValue);
    } else if (reason === 'clear') {
      setInputValue('');
      setOptions([]);
    }
  };

  const onChangeHandler = (
    _event: React.SyntheticEvent,
    newValue: Option | null,
  ) => {
    if (newValue) {
      setInputValue(newValue.position.properties.name);
    }
    if (onChange) {
      onChange(newValue?.position || null);
    }
  };

  const debouncedUpdateOptions = useRef(
    debounce(async (query: string) => {
      if (query.length < MIN_QUERY_LENGTH) {
        setOptions([]);
        return;
      }
      setLoading(true);
      const opts = await fetchOptionsFromApiPhoton(query);
      setOptions(opts);
      setLoading(false);
    }, DEBOUNCE_MS),
  ).current;

  return (
    <Autocomplete
      inputValue={inputValue}
      options={options}
      open={options.length > 0}
      onClose={() => setOptions([])}
      getOptionKey={(option) => option.key}
      getOptionLabel={(x) => x.position.properties.name}
      filterOptions={(x) => x} // Disable built-in filtering since we use API
      slotProps={{ paper: { sx: { minWidth: 'max-content' } } }} // dynamic width for autocomplete items
      size={size}
      renderInput={(params) =>
        renderInput({
          params,
          label,
          loading,
          placeholder,
          sx,
          required,
          error,
          helperText,
        })
      }
      renderOption={(props, option) => renderOption({ props, option })}
      onInputChange={onInputChange}
      onChange={onChangeHandler}
      forcePopupIcon={false}
      clearOnBlur={false}
    />
  );
}

function renderOption({
  props,
  option,
}: {
  props: React.HTMLAttributes<HTMLLIElement> & { key: string };
  option: Option;
}) {
  const value = option.position.properties.address;
  return (
    <MenuItem {...props} value={value} key={option.key}>
      {value}
    </MenuItem>
  );
}
// TODO later : better format here : with icon dependinf on type of option...

function renderInput({
  params,
  label,
  placeholder,
  sx,
  required,
  loading,
  error,
  helperText,
}: {
  params: AutocompleteRenderInputParams;
  label?: string;
  placeholder?: string;
  sx?: SxProps<Theme>;
  required?: boolean;
  loading: boolean;
  error?: boolean;
  helperText?: string;
}) {
  return (
    <TextField
      {...params}
      label={label}
      placeholder={placeholder}
      required={required}
      error={error}
      helperText={helperText}
      sx={{ my: 1, ...sx }}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {loading ? <CircularProgress color="inherit" size={20} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );
}
